import { getFirestore, collection, getDocs, query, orderBy, limit, getCountFromServer } from "firebase/firestore";
import { app } from "../firebase-config.js";

const db = getFirestore(app);

export const AdminService = {
    /**
     * Aggregates platform-wide statistics for the Admin Dashboard.
     * Returns KPIs, Top Series, and Chart Data.
     */
    getDashboardStats: async () => {
        const stats = {
            totalUsers: 0,
            totalSeries: 0,
            totalViews: 0,
            totalLikes: 0,
            pendingRequests: 0
        };

        try {
            // 1. Define Queries
            const usersQ = query(collection(db, "users"));
            const seriesQ = query(collection(db, "series"));
            const requestQ = query(collection(db, 'author_requests'), where('status', '==', 'pending'));

            // 2. Fetch All (Fail-Safe)
            const results = await Promise.allSettled([
                getDocs(usersQ),      // 0
                getDocs(seriesQ),     // 1
                getDocs(requestQ)     // 2
            ]);

            // 3. Process Users
            if (results[0].status === 'fulfilled') {
                stats.totalUsers = results[0].value.size;
            } else {
                console.error("Users Fetch Failed:", results[0].reason);
            }

            // 4. Process Series (Count + Views + Likes)
            let allSeries = [];
            if (results[1].status === 'fulfilled') {
                const snap = results[1].value;
                stats.totalSeries = snap.size;

                snap.forEach(doc => {
                    const data = doc.data();
                    const v = data.stats?.viewCount || data.viewCount || 0;
                    const l = data.stats?.likeCount || data.likeCount || 0;
                    stats.totalViews += v;
                    stats.totalLikes += l;
                    allSeries.push({ id: doc.id, ...data, viewCount: v, likeCount: l });
                });
            } else {
                console.error("Series Fetch Failed:", results[1].reason);
            }

            // 5. Process Requests
            if (results[2].status === 'fulfilled') {
                stats.pendingRequests = results[2].value.size;
            } else {
                console.warn("Requests Fetch Failed (Might be permission/empty):", results[2].reason);
            }

            // 6. Top Series (Sort)
            const topSeries = allSeries
                .sort((a, b) => b.viewCount - a.viewCount)
                .slice(0, 5);

            // 7. Mock Chart (Keep as is)
            const chartData = {
                labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
                datasets: [{
                    label: 'Weekly Traffic',
                    data: [150, 230, 180, 320, 290, 450, 500],
                    borderColor: '#4f46e5',
                    backgroundColor: 'rgba(79, 70, 229, 0.1)',
                    borderWidth: 2,
                    fill: true,
                    tension: 0.4
                }]
            };

            return { summary: stats, topSeries, chartData };

        } catch (error) {
            console.error("AdminService Critical Error:", error);
            // Return zeros instead of throwing to prevent UI crash
            return {
                summary: stats,
                topSeries: [],
                chartData: null
            };
        }
    },

    /**
     * Run Weekly Hall of Fame Batch (Simulated)
     * Selects Top 3 Users based on Points/Activity
     */
    /**
     * Run Weekly Hall of Fame Batch (Real)
     * Prioritizes users with 'hall_of_fame' badge.
     */
    /**
     * Run Weekly Hall of Fame Batch (Real)
     * Prioritizes users with 'hall_of_fame' badge.
     * Uses Cloud Function to bypass Firestore Security Rules.
     */
    runWeeklyHallOfFame: async () => {
        try {
            const { getFunctions, httpsCallable } = await import("firebase/functions");
            const functions = getFunctions(app, 'us-central1');
            const getHallOfFameFn = httpsCallable(functions, 'getHallOfFame');

            const result = await getHallOfFameFn();
            return result.data;

        } catch (e) {
            // Guest or Error
            if (e.code === 'permission-denied' || e.code === 'unauthenticated' || e.message.includes('unauthenticated')) {
                return [];
            }
            console.error("Hall of Fame Error:", e);
            return [];
        }
    },

    /**
     * Emergency Utils: Reset All Star Points (v0.865)
     * Resets starPoints of ALL users to 0.
     */
    resetAllStarPoints: async () => {
        try {
            const userQ = query(collection(db, "users"));
            const snapshot = await getDocs(userQ);
            const { writeBatch } = await import("firebase/firestore");

            const batch = writeBatch(db);
            let count = 0;

            snapshot.forEach(d => {
                batch.update(d.ref, { starPoints: 0 });
                count++;
            });

            await batch.commit();
            return count;
        } catch (e) {
            console.error("Reset Failed:", e);
            throw e;
        }
    }

};
